import { useEffect, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, X } from 'lucide-react';
import { Botao } from './base';
import { escFechaAviso, useFecharAvisoFixo } from './toast';
import {
  VERSAO_WEB,
  assinarNovaVersao,
  buscarVersaoNoAr,
  criarVigiaVersao,
  dispensarNovaVersao,
  ehFalhaDeChunk,
  estadoNovaVersao,
  recarregarPagina,
  sinalizarNovaVersao,
} from '../nucleo/versao';

/**
 * Fase 2 (W39): liga a vigia da versão (5 min e ao voltar o foco) e os sinais de chunk que não carregou (`vite:preloadError`
 * e a promessa rejeitada de um import dinâmico). Uma vez por aba.
 */
function useVigiaNovaVersao() {
  useEffect(() => {
    const vigia = criarVigiaVersao({ versaoBuild: VERSAO_WEB, buscar: () => buscarVersaoNoAr(), aoMudar: () => sinalizarNovaVersao('versao') });
    vigia.iniciar();
    const aoVisivel = () => document.visibilityState === 'visible' && vigia.aoFocar();
    const aoFoco = () => vigia.aoFocar();
    // O Vite dispara `vite:preloadError` quando um pedaço não carrega; sem o preventDefault, o erro sobe e quebra a tela.
    const aoPreload = (e: Event) => {
      e.preventDefault();
      sinalizarNovaVersao('chunk');
    };
    const aoRejeitar = (e: PromiseRejectionEvent) => {
      if (!ehFalhaDeChunk(e.reason)) return;
      e.preventDefault();
      sinalizarNovaVersao('chunk');
    };
    document.addEventListener('visibilitychange', aoVisivel);
    window.addEventListener('focus', aoFoco);
    window.addEventListener('vite:preloadError', aoPreload);
    window.addEventListener('unhandledrejection', aoRejeitar);
    return () => {
      vigia.parar();
      document.removeEventListener('visibilitychange', aoVisivel);
      window.removeEventListener('focus', aoFoco);
      window.removeEventListener('vite:preloadError', aoPreload);
      window.removeEventListener('unhandledrejection', aoRejeitar);
    };
  }, []);
}

/**
 * O aviso "Nova versão disponível", na região de avisos (o mesmo cartão dos avisos curtos), com "Atualizar". Não some
 * sozinho e não pega o foco: a pessoa termina o que está fazendo e atualiza quando quiser (ou fecha no X).
 * Review r25 visual: Esc com o foco no aviso fecha só ele (não o modal de baixo) e, ao fechar, o foco volta para onde estava
 * antes do F8 (M1), como nos avisos curtos; "Atualizar" é secundário, o cartão do toast não tem primário (B1).
 */
export function AvisoNovaVersao() {
  const { t } = useTranslation();
  useVigiaNovaVersao();
  const estado = useSyncExternalStore(assinarNovaVersao, estadoNovaVersao, estadoNovaVersao);
  const fechar = useFecharAvisoFixo(dispensarNovaVersao);
  if (!estado.visivel) return null;
  return (
    <div
      data-toast
      data-nova-versao={estado.motivo ?? ''}
      className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-borda bg-sup-2 p-3 text-sm shadow-lg"
      onKeyDown={escFechaAviso(fechar)}
    >
      <RefreshCw className="mt-0.5 size-4 shrink-0 text-acento-forte" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium break-words text-tinta-1">{t('novaVersao.texto')}</p>
        <p className="mt-0.5 break-words text-tinta-3">{t('novaVersao.detalhe')}</p>
        <Botao variante="secundario" tamanho="sm" className="mt-2" onClick={recarregarPagina} data-atualizar-versao>
          {t('novaVersao.atualizar')}
        </Botao>
      </div>
      <button type="button" data-fechar-aviso aria-label={t('avisos.fechar')} onClick={fechar} className="rounded text-tinta-3 hover:text-tinta-1">
        <X className="size-4" />
      </button>
    </div>
  );
}
